/* eslint-disable no-redeclare */
import { searchSettings } from '../../../config';
import { NotFoundError } from '../../../errors/not-found-error';
import { authGuard } from '../../../utils/auth-utils';
import { URLPath } from '../../../utils/commonUtils';
import { decodeURIComponentSafe, isStringEncoded } from '../../../utils/searchUtils';
import { InputContext } from '../../../views/ats-details/use-input-context';
import { queryClient } from '../../client';
import { genresQuery } from '../../genres/queries';
import { GenreItem } from '../../genres/types';
import { AtsHttpClient } from '../ats-http-client';
import { bookmarksQuery } from '../me/bookmarks/queries';
import { subscribedProductsQuery } from '../me/products/queries';
import { providersQuery } from '../me/providers/queries';
import { recordingsQuery } from '../recordings/queries';
import { Recording } from '../recordings/types/recording';
import { createEpisodeRecordingTitle, createRecordingEpisodeTitles } from '../recordings/utils/title-adapter';
import { settings } from '../settings';
import { titleQuery } from './queries';
import { Asset, AssetDependencies, EpisodeAsset, MovieAsset, ProgramAsset, SeriesAsset } from './types/asset';
import { SearchResults } from './types/search';
import { EpisodeTitle, MovieTitle, ProgramTitle, SeriesTitle, Title } from './types/title';
import { createEpisodes } from './utils/asset-utils';
import { findMatchingSeriesRecording, getSeriesRecordingEpisodes, mergeEpisodes } from './utils/recording-utils';
import { createSources, sourcesFromSeries } from './utils/sources-utils';

const atsHttpClient = new AtsHttpClient();

export interface FetchAssetOptions extends Omit<InputContext, 'platformTitleId'> {}

export async function getAsset(id: string, options?: FetchAssetOptions): Promise<Asset> {
  try {
    const title = await queryClient.fetchQuery(titleQuery({ id, ...options }));
    return createAssetNoOverload(title);
  } catch (error) {
    if (error.status === 404) {
      throw new NotFoundError(id);
    } else {
      throw error;
    }
  }
}

export async function createAssetNoOverload(title: Title): Promise<Asset> {
  // This seems weird, but is required to make TypeScript happy,
  // because we overload `createAsset` with different types.
  switch (title.type) {
    case 'movie':
      return createAsset(title);
    case 'series':
      return createAsset(title);
    case 'episode':
      return createAsset(title);
    case 'program':
      return createAsset(title);
    default:
      throw new Error('Unknown title type');
  }
}

export async function createAsset(program: ProgramTitle): Promise<ProgramAsset>;
export async function createAsset(movie: MovieTitle): Promise<MovieAsset>;
export async function createAsset(series: SeriesTitle): Promise<SeriesAsset>;
export async function createAsset(episode: EpisodeTitle): Promise<EpisodeAsset>;
export async function createAsset(title: Title): Promise<Asset> {
  const [providers, recordings, products, bookmarks, genres] = await getAssetDependencies();

  if (title.type === 'series') {
    return createSeriesAssets({
      title,
      dependencies: [providers, recordings, products, bookmarks, genres],
    });
  }

  return {
    ...title,
    sources: createSources(title.playbackOptions, providers, recordings, products, bookmarks),
  };
}

export function swallowError<T, F>(cb: () => Promise<T>, fallback: F): Promise<T> | Promise<F> {
  try {
    return cb();
  } catch (error) {
    return Promise.resolve(fallback);
  }
}

export async function getAssetDependencies(): Promise<AssetDependencies> {
  const [providers, recordings, products, bookmarks, genres] = await Promise.allSettled([
    queryClient.fetchQuery(providersQuery()),
    authGuard({
      type: ['home', 'user'],
      callback: () => queryClient.fetchQuery(recordingsQuery()),
      fallback: [],
    }),
    authGuard({
      type: ['home', 'user'],
      callback: () => queryClient.fetchQuery(subscribedProductsQuery()),
      fallback: [],
    }),
    authGuard({
      type: 'user',
      callback: () => queryClient.fetchQuery(bookmarksQuery()),
      fallback: [],
    }),
    queryClient.fetchQuery(genresQuery()),
  ]);

  if (providers.status === 'rejected') {
    throw providers.reason;
  }

  return [
    providers.value,
    recordings.status === 'fulfilled' ? recordings.value : [],
    products.status === 'fulfilled' ? products.value : [],
    bookmarks.status === 'fulfilled' ? bookmarks.value : [],
    genres.status === 'fulfilled' ? genres.value : [],
  ];
}

interface CreateSeriesAssetsOptions {
  title: SeriesTitle;
  dependencies: AssetDependencies;
}

export function createSeriesAssets(options: CreateSeriesAssetsOptions) {
  const [providers, recordings, products, bookmarks, genres] = options.dependencies;

  const sources = sourcesFromSeries(options.title, providers);

  const matchingRecording = findMatchingSeriesRecording(options.title.episodes, recordings);

  if (matchingRecording !== undefined) {
    const recordingEpisodeTitles = createRecordingEpisodeTitles(matchingRecording, genres);
    const singleEpisodeTitles = findSingleRecordedTitles(options.title, recordings, genres);
    const mergedEpisodes = mergeEpisodes(recordingEpisodeTitles, options.title.episodes, singleEpisodeTitles);
    const episodes = createEpisodes(mergedEpisodes, providers, recordings, products, bookmarks);

    return {
      ...options.title,
      episodes: episodes,
      sources: sources,
    };
  }
  const episodes = findSingleRecordedTitles(options.title, recordings, genres);
  const mergedEpisodes = mergeEpisodes(options.title.episodes, episodes);

  return {
    ...options.title,
    episodes: createEpisodes(mergedEpisodes, providers, recordings, products, bookmarks),
    sources: sources,
  };
}

function findSingleRecordedTitles(series: SeriesTitle, recordings: Recording[], genres: GenreItem[]) {
  const titles: EpisodeTitle[] = [];
  const seen = new Set<string>();

  const episodeOptions = series.episodes.flatMap((episode) => episode.playbackOptions);
  const options = series.playbackOptions.concat(episodeOptions);

  for (const option of options) {
    if ('seriesId' in option && option.seriesId !== undefined) {
      const recordedEpisodes = getSeriesRecordingEpisodes(recordings, option.seriesId, option.lifetimeId);

      for (const ep of recordedEpisodes) {
        if (!seen.has(ep.id)) {
          seen.add(ep.id);
          titles.push(createEpisodeRecordingTitle(ep, genres));
        }
      }
    }
  }

  return titles;
}

function fetchByPath<T>(segments: string[], search?: object, signal?: AbortSignal) {
  const path = new URLPath(...segments);

  if (search) {
    for (const [key, value] of Object.entries(search)) {
      if (value !== undefined) {
        path.search.set(key, value);
      }
    }
  }

  return atsHttpClient.get<T>(path.toString(), { ...settings.titles('GET'), signal });
}

export interface FetchTitleOptions {
  orderByProviderId?: string;
  orderByServiceType?: string;
  signal?: AbortSignal;
}

export async function fetchTitleById(id: string, options?: FetchTitleOptions): Promise<Title> {
  // The `masterId` is a UUID, which is 36 characters long.
  const isMasterId = id.length === 36;

  const params = {
    orderByProviderId: options?.orderByProviderId,
    orderByServiceType: options?.orderByServiceType,
  };

  if (isMasterId) {
    return fetchByPath<Title>(['titles', id], params, options?.signal);
  }

  return fetchByPath<Title>(['titles', 'byPlatformTitleId', id], params, options?.signal);
}

export async function fetchTitle(id: string, options?: FetchTitleOptions) {
  const title = await fetchTitleById(id, options);

  if (title.type === 'series') {
    const episodes = await fetchEpisodes(title.masterId, options).catch(() => []);
    return { ...title, episodes };
  }

  return title;
}

export async function searchQueryByTitles(
  query: string,
  page: number = 0,
  take = searchSettings.resultsExpandLength,
): Promise<SearchResults> {
  if (isStringEncoded(query)) {
    query = decodeURIComponentSafe(encodeURIComponent(query));
  }

  const skip = page * take;
  const body = `queryText=${query}&skip=${skip}&take=${take}`;

  try {
    const results = await atsHttpClient.get<SearchResults>(`/titles/search?${body}`, settings.titles('GET'));
    return { ...results, page };
  } catch {
    // ATS Requests fails when some characters are passed return empty search results when call fails
    return Promise.resolve({ ...({} as SearchResults), page });
  }
}

export async function fetchEpisodes(id: string, options?: FetchTitleOptions): Promise<EpisodeTitle[]> {
  const params = {
    orderByProviderId: options?.orderByProviderId,
    orderByServiceType: options?.orderByServiceType,
  };

  return fetchByPath<EpisodeTitle[]>(['titles', 'series', id, 'episodes'], params);
}

export async function getTitleSuggestions(queryString: string): Promise<string[]> {
  return atsHttpClient.get<string[]>(`/titles/suggest?queryText=${queryString}`, settings.titlesSuggest());
}
